<script setup lang="ts">
import { ref, computed, onBeforeUnmount, onMounted } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import { useAuthStore } from './stores/auth'
import type { FeatureModule } from './types/featureModules'

const router = useRouter()
const authStore = useAuthStore()
const { mobile } = useDisplay()

const drawer = ref(true)
const fabOpen = ref(false)

const drawerTemporary = computed(() => mobile.value)
const homeRoute = computed(() => authStore.defaultRoute)
const canAccess = (module: FeatureModule) => authStore.canAccessModule(module)

// Accordion: track open groups, enforce single top-level open at a time
const topLevelGroups = ['trading', 'sentinel', 'productivity', 'finance', 'admin']
const openedGroups = ref<string[]>([])

const handleGroupsUpdate = (newVal: string[]) => {
  const newTopLevel = newVal.find(v => topLevelGroups.includes(v) && !openedGroups.value.includes(v))
  if (newTopLevel) {
    // A new top-level group was opened — collapse all others and their sub-groups
    openedGroups.value = [newTopLevel]
  } else {
    openedGroups.value = newVal
  }
}

const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}

const handleModuleDenied = async (event: Event) => {
  const deniedModule = (event as CustomEvent<{ module?: string }>).detail?.module
  await authStore.fetchUser()
  await router.push({ name: 'access-pending', query: deniedModule ? { denied: deniedModule } : {} })
}

onMounted(() => window.addEventListener('module-access-denied', handleModuleDenied))
onBeforeUnmount(() => window.removeEventListener('module-access-denied', handleModuleDenied))
</script>

<template>
  <v-app>
    <!-- Guest nav icons (floating, no bar) -->
    <div v-if="!authStore.isAuthenticated" class="guest-nav">
      <v-btn icon to="/login" size="small" variant="text" color="white">
        <v-icon>mdi-login</v-icon>
      </v-btn>
      <v-btn icon to="/register" size="small" variant="text" color="white">
        <v-icon>mdi-account-plus</v-icon>
      </v-btn>
    </div>

    <!-- Mobile drawer toggle (replaces hamburger from removed AppBar) -->
    <v-btn
      v-if="authStore.isAuthenticated && mobile && !drawer"
      icon
      size="small"
      color="#1a2332"
      class="mobile-menu-btn"
      @click="drawer = !drawer"
    >
      <v-icon>mdi-menu</v-icon>
    </v-btn>

    <v-navigation-drawer
      v-if="authStore.isAuthenticated"
      v-model="drawer"
      :temporary="drawerTemporary"
      width="260"
      color="#1a2332"
      theme="dark"
    >
      <v-list-item
        class="pa-4"
        :to="homeRoute"
      >
        <template v-slot:prepend>
          <img src="/favicon.png" alt="Logo" style="height: 36px; width: 36px; object-fit: contain;" />
        </template>
        <v-list-item-title class="text-subtitle-2 font-weight-bold text-white">Shadow Forex</v-list-item-title>
        <v-list-item-subtitle class="text-caption" style="color: rgba(255,255,255,0.5);">Personal Dashboard</v-list-item-subtitle>
      </v-list-item>

      <v-divider color="rgba(255,255,255,0.1)" />

      <v-list density="compact" nav class="pa-2 sidebar-nav" open-strategy="multiple" :opened="openedGroups" @update:opened="handleGroupsUpdate" style="--list-indent-size: 0px;">
        <!-- Dashboard -->
        <v-list-item
          v-if="canAccess('sentinel')"
          prepend-icon="mdi-view-dashboard-outline"
          title="Dashboard"
          value="home"
          to="/"
          rounded="lg"
          active-color="primary"
        />

        <!-- Trading Group -->
        <v-list-group v-if="canAccess('trading')" value="trading">
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              prepend-icon="mdi-chart-line"
              title="Trading"
              rounded="lg"
              class="mt-1"
            />
          </template>

          <!-- Calculators Sub-group -->
          <v-list-group value="calculators">
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                prepend-icon="mdi-calculator-variant"
                title="Calculators"
                rounded="lg"
              />
            </template>

            <v-list-item
              prepend-icon="mdi-auto-fix"
              title="Auto Lot Sizing"
              to="/calculators/module-a"
              rounded="lg"
              active-color="primary"
            />
            <v-list-item
              prepend-icon="mdi-pencil-ruler"
              title="Manual Lot Sizing"
              to="/calculators/module-b"
              rounded="lg"
              active-color="primary"
            />
            <v-list-item
              prepend-icon="mdi-tune"
              title="Full Manual Control"
              disabled
              rounded="lg"
              class="nav-disabled"
            >
              <template v-slot:append>
                <v-icon size="8" color="warning">mdi-circle</v-icon>
              </template>
            </v-list-item>
            <v-list-item
              prepend-icon="mdi-scale-balance"
              title="Trade RRR"
              disabled
              rounded="lg"
              class="nav-disabled"
            >
              <template v-slot:append>
                <v-icon size="8" color="warning">mdi-circle</v-icon>
              </template>
            </v-list-item>
          </v-list-group>

          <!-- Analysis Tools Sub-group -->
          <v-list-group value="analysis">
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                prepend-icon="mdi-chart-bar"
                title="Analysis Tools"
                rounded="lg"
              />
            </template>

            <v-list-item
              prepend-icon="mdi-shield-alert-outline"
              title="Risk Analyzer"
              to="/tools/max-levels"
              rounded="lg"
              active-color="primary"
            />
            <v-list-item
              prepend-icon="mdi-chart-bell-curve"
              title="Fibonacci Helper"
              disabled
              rounded="lg"
              class="nav-disabled"
            >
              <template v-slot:append>
                <v-icon size="8" color="warning">mdi-circle</v-icon>
              </template>
            </v-list-item>
            <v-list-item
              prepend-icon="mdi-format-list-numbered"
              title="Shadow Entry Guide"
              disabled
              rounded="lg"
              class="nav-disabled"
            >
              <template v-slot:append>
                <v-icon size="8" color="warning">mdi-circle</v-icon>
              </template>
            </v-list-item>
            <v-list-item
              prepend-icon="mdi-counter"
              title="Pip Count Tracker"
              disabled
              rounded="lg"
              class="nav-disabled"
            >
              <template v-slot:append>
                <v-icon size="8" color="warning">mdi-circle</v-icon>
              </template>
            </v-list-item>
          </v-list-group>

          <!-- MetaTrader Sub-group -->
          <v-list-group value="metatrader">
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                prepend-icon="mdi-monitor-dashboard"
                title="MetaTrader"
                rounded="lg"
              />
            </template>

            <v-list-item
              prepend-icon="mdi-chart-timeline-variant"
              title="Trade Dashboard"
              to="/mt4/dashboard"
              rounded="lg"
              active-color="primary"
            />
            <v-list-item
              prepend-icon="mdi-key-variant"
              title="API Keys"
              to="/mt4/api-keys"
              rounded="lg"
              active-color="primary"
            />
          </v-list-group>
        </v-list-group>

        <!-- Sentinel Group -->
        <v-list-group v-if="canAccess('sentinel')" value="sentinel">
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              prepend-icon="mdi-shield-search"
              title="Sentinel"
              rounded="lg"
              class="mt-1"
            />
          </template>

          <v-list-item
            prepend-icon="mdi-view-dashboard"
            title="Dashboard"
            to="/sentinel"
            rounded="lg"
            active-color="primary"
          />
          <v-list-item
            prepend-icon="mdi-radar"
            title="Signal Feed"
            to="/sentinel/signals"
            rounded="lg"
            active-color="primary"
          />
          <v-list-item
            prepend-icon="mdi-bell-alert-outline"
            title="Alert Rules"
            to="/sentinel/alerts"
            rounded="lg"
            active-color="primary"
          />
          <v-list-item
            v-if="authStore.user?.is_admin"
            prepend-icon="mdi-database-cog-outline"
            title="Sources"
            to="/sentinel/sources"
            rounded="lg"
            active-color="primary"
          />
        </v-list-group>

        <!-- Productivity Group -->
        <v-list-group v-if="canAccess('productivity')" value="productivity">
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              prepend-icon="mdi-clipboard-check-outline"
              title="Productivity"
              rounded="lg"
              class="mt-1"
            />
          </template>

          <v-list-item
            prepend-icon="mdi-view-dashboard-outline"
            title="Dashboard"
            to="/productivity/dashboard"
            rounded="lg"
            active-color="primary"
          />
          <v-list-item
            prepend-icon="mdi-format-list-checks"
            title="Tasks"
            to="/todos"
            rounded="lg"
            active-color="primary"
          />
          <v-list-item
            prepend-icon="mdi-flag-variant-outline"
            title="Projects"
            to="/productivity/projects"
            rounded="lg"
            active-color="primary"
          />
          <v-list-item
            prepend-icon="mdi-repeat"
            title="Routines"
            to="/productivity/routines"
            rounded="lg"
            active-color="primary"
          />
          <v-list-item
            prepend-icon="mdi-compass-outline"
            title="Life Areas"
            to="/productivity/life-areas"
            rounded="lg"
            active-color="primary"
          />
          <v-list-item
            prepend-icon="mdi-notebook-edit-outline"
            title="Weekly Review"
            to="/productivity/review"
            rounded="lg"
            active-color="primary"
          />
          <v-list-item
            prepend-icon="mdi-chart-box-outline"
            title="Analytics"
            to="/productivity/insights"
            rounded="lg"
            active-color="primary"
          />
        </v-list-group>

        <!-- Finance Group -->
        <v-list-group v-if="canAccess('finance')" value="finance">
          <template v-slot:activator="{ props }">
            <v-list-item
              v-bind="props"
              prepend-icon="mdi-wallet-outline"
              title="Finance"
              rounded="lg"
              class="mt-1"
            />
          </template>

          <v-list-item
            prepend-icon="mdi-chart-donut"
            title="Budget Tracker"
            to="/finance/budget-tracker"
            rounded="lg"
            active-color="primary"
          />
          <v-list-item
            prepend-icon="mdi-file-percent-outline"
            title="Paystub & Tax"
            to="/finance/paystub-tax"
            rounded="lg"
            active-color="primary"
          />
          <v-list-item
            prepend-icon="mdi-receipt-text-outline"
            title="Expense Logs"
            disabled
            rounded="lg"
            class="nav-disabled"
          >
            <template v-slot:append>
              <v-icon size="8" color="warning">mdi-circle</v-icon>
            </template>
          </v-list-item>
        </v-list-group>

        <v-list-item
          v-if="canAccess('documentation')"
          prepend-icon="mdi-book-open-variant"
          title="Documentation"
          to="/documentation"
          rounded="lg"
          active-color="primary"
          class="mt-1"
        />

        <!-- Admin Group -->
        <template v-if="authStore.user?.is_admin">
          <v-list-group value="admin">
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                prepend-icon="mdi-cog-outline"
                title="Admin"
                rounded="lg"
                class="mt-1"
              />
            </template>

            <v-list-item
              prepend-icon="mdi-account-group-outline"
              title="User Management"
              to="/admin/users"
              rounded="lg"
              active-color="primary"
            />
            <v-list-item
              prepend-icon="mdi-license"
              title="RecoveryEA Licenses"
              to="/admin/licenses"
              rounded="lg"
              active-color="primary"
            />
          </v-list-group>
        </template>
      </v-list>

      <template v-slot:append>
        <v-divider color="rgba(255,255,255,0.1)" />
        <v-list density="compact" nav class="pa-2">
          <v-list-item
            v-if="authStore.isAuthenticated"
            rounded="lg"
          >
            <template v-slot:prepend>
              <v-avatar size="32" color="primary">
                <span class="text-white text-caption font-weight-bold">{{ authStore.user?.name?.charAt(0)?.toUpperCase() }}</span>
              </v-avatar>
            </template>
            <v-list-item-title class="text-body-2 text-white">{{ authStore.user?.name || 'Account' }}</v-list-item-title>
            <v-list-item-subtitle class="text-caption" style="color: rgba(255,255,255,0.5);">{{ authStore.user?.email }}</v-list-item-subtitle>
          </v-list-item>
          <v-list-item
            v-if="authStore.isAuthenticated"
            prepend-icon="mdi-logout"
            title="Logout"
            @click="handleLogout"
            rounded="lg"
            base-color="error"
          />
        </v-list>
      </template>
    </v-navigation-drawer>

    <v-main :class="{ 'mobile-main-spacing': authStore.isAuthenticated && mobile, 'guest-main': !authStore.isAuthenticated }">
      <RouterView />
    </v-main>

    <!-- Quick Action FAB -->
    <div
      v-if="authStore.isAuthenticated && (canAccess('trading') || canAccess('productivity'))"
      class="fab-container"
    >
      <v-menu v-model="fabOpen" location="top" :close-on-content-click="true">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            size="56"
            color="primary"
            elevation="8"
            class="fab-button"
          >
            <v-icon :class="{ 'fab-icon-rotate': fabOpen }">mdi-plus</v-icon>
          </v-btn>
        </template>

        <v-list density="compact" class="fab-menu" rounded="lg" elevation="8">
          <v-list-item
            v-if="canAccess('trading')"
            prepend-icon="mdi-calculator-variant"
            title="Quick Calculate"
            to="/calculators/module-a"
            rounded="lg"
          />
          <v-list-item
            v-if="canAccess('productivity')"
            prepend-icon="mdi-plus-circle-outline"
            title="Add To-Do"
            to="/todos"
            rounded="lg"
          />
        </v-list>
      </v-menu>
    </div>
  </v-app>
</template>

<style scoped>
.guest-nav {
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 1005;
  display: flex;
  gap: 4px;
}

.mobile-menu-btn {
  position: fixed;
  top: 12px;
  left: 12px;
  z-index: 1005;
}

.mobile-main-spacing {
  padding-top: 56px !important;
}

.guest-main {
  min-height: 100dvh;
}

.guest-main :deep(.v-main__wrap) {
  display: flex;
  flex: 1;
  min-height: inherit;
}

/* Base nav items */
.sidebar-nav :deep(.v-list-item) {
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1px;
  min-height: 38px;
  font-size: 0.875rem;
}

.sidebar-nav :deep(.v-list-item:hover) {
  color: rgba(255, 255, 255, 0.9);
}

.sidebar-nav :deep(.v-list-item--active) {
  color: white;
}

/* Reset Vuetify's cumulative indent on all nested group containers */
.sidebar-nav :deep(.v-list-group__items) {
  --indent-padding: 0px !important;
  --parent-padding: 0px !important;
}

/* Level 1: Top-level group activators (Trading, Sentinel, etc.) */
.sidebar-nav :deep(.v-list-group > .v-list-group__items > .v-list-item),
.sidebar-nav :deep(.v-list-group > .v-list-group__items > .v-list-group > div > .v-list-item) {
  padding-inline-start: 16px !important;
  font-size: 0.8125rem;
}

/* Level 1 children container — vertical guide line */
.sidebar-nav :deep(.v-list-group > .v-list-group__items) {
    position: relative;
  margin-left: 20px;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
}

/* Level 2: Nested sub-group items (Auto Lot Sizing, Risk Analyzer, etc.) */
.sidebar-nav :deep(.v-list-group > .v-list-group__items .v-list-group__items .v-list-item) {
  padding-inline-start: 12px !important;
  font-size: 0.775rem;
}

/* Level 2 children container — deeper guide line */
.sidebar-nav :deep(.v-list-group > .v-list-group__items .v-list-group__items) {
  position: relative;
  background: rgba(255, 255, 255, 0.04);
}

/* Active item — teal left border indicator */
.sidebar-nav :deep(.v-list-item--active) {
  border-left: 2px solid rgb(var(--v-theme-primary)) !important;
  color: white;
}

/* Disabled / coming soon items */
.nav-disabled {
  opacity: 0.4 !important;
}

/* Sub-group activator icons slightly smaller */
.sidebar-nav :deep(.v-list-group__items .v-list-group .v-list-item .v-icon) {
  font-size: 18px;
}

/* FAB */
.fab-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
}

.fab-button {
  transition: transform 0.2s ease;
}

.fab-button:hover {
  transform: scale(1.05);
}

.fab-icon-rotate {
  transform: rotate(45deg);
  transition: transform 0.2s ease;
}

.fab-menu {
  min-width: 180px;
}
</style>
